import React from "react";
import {
  SimpleForm,
  TextInput,
  PasswordInput,
  ReferenceArrayInput,
  SelectArrayInput,
  ChipField,
  Create,
  required,
  minLength,
  email,
} from "react-admin";
import Tooltip from "@material-ui/core/Tooltip";
import InfoIcon from "@material-ui/icons/Info";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import CreateToolbar from "../Custom/create-save-button";
import theme from "../../customTheme";
import { useCanManageUserPassword } from "./userRoleUtils";
import { PASSWORD_PATTERN, PASSWORD_HINT } from "./change-password";

const validateName = [required(), minLength(2)];
const validateEmail = [required(), email()];
const validateRoles = required();

const validatePasswordStrength = (value) => {
  if (!value) return "Password is required";
  if (!PASSWORD_PATTERN.test(value)) return PASSWORD_HINT;
  return undefined;
};

const validateConfirmPassword = (value, allValues) => {
  if (!value) return "Please confirm your password";
  if (value !== allValues.password) return "The passwords do not match";
  return undefined;
};

const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
  },
  roles: {
    width: "100%",
  },
  sectionTitle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
  },
  infoIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    lineHeight: 0,
  },
  infoIcon: {
    fontSize: 16,
  },
  passwordPanel: {
    paddingLeft: 24,
    borderLeft: "1px solid #e0e0e0",
    width: "100%",
  },
  marginBounds: {
    marginTop: "-20px",
  },
}));

const UserCreateContent = (props) => {
  const classes = useStyles();
  const { canManage: showPasswordSection } = useCanManageUserPassword();

  return (
    <SimpleForm {...props} toolbar={<CreateToolbar />}>
      <Grid container spacing={3} className={classes.container}>
        <Grid item xs={12} md={showPasswordSection ? 6 : 12}>
          <TextInput source="email" validate={validateEmail} fullWidth />
          <TextInput source="name" validate={validateName} fullWidth />
          <ReferenceArrayInput
            label="Roles"
            source="roles"
            reference="roles"
            resource="roles"
          >
            <SelectArrayInput
              validate={validateRoles}
              className={classes.roles}
            >
              <ChipField source="name" />
            </SelectArrayInput>
          </ReferenceArrayInput>

          {!showPasswordSection && (
            <Grid container alignItems="center">
              <PasswordInput
                source="password"
                validate={[required(), validatePasswordStrength]}
              />
              <Tooltip title={PASSWORD_HINT} arrow leaveDelay={200}>
                <InfoIcon
                  htmlColor={theme.palette.secondary.dark}
                  className={classes.marginBounds}
                />
              </Tooltip>
            </Grid>
          )}
        </Grid>

        {showPasswordSection && (
          <Grid item xs={12} md={6}>
            <div className={classes.passwordPanel}>
              <Typography variant="h6" className={classes.sectionTitle}>
                Set Password
                <Tooltip title={PASSWORD_HINT} arrow leaveDelay={200}>
                  <span className={classes.infoIconWrap}>
                    <InfoIcon
                      htmlColor={theme.palette.secondary.dark}
                      className={classes.infoIcon}
                    />
                  </span>
                </Tooltip>
              </Typography>
              <PasswordInput
                source="password"
                label="New Password"
                validate={[required(), validatePasswordStrength]}
                fullWidth
              />
              <PasswordInput
                source="confirmPassword"
                label="Confirm Password"
                validate={[required(), validateConfirmPassword]}
                fullWidth
              />
            </div>
          </Grid>
        )}
      </Grid>
    </SimpleForm>
  );
};

const UserCreate = (props) => {
  return (
    <Create {...props}>
      <UserCreateContent />
    </Create>
  );
};

export default UserCreate;
